import { isWeb } from '../../utils/platformUtils'
import { PORTFOLIO_STORE_NAMES } from '../constants'

export const addCategory = (db, category) => {
    return new Promise((resolve, reject) => {
        if (isWeb()) {
            // Web implementation using IndexedDB
            // Remove duplicate brands before saving
            const cleanCategory = {
                ...category,
                brands: category.brands ? [...new Set(category.brands)] : [],
            }

            const transaction = db.transaction([PORTFOLIO_STORE_NAMES.CATEGORIES], 'readwrite')
            const objectStore = transaction.objectStore(PORTFOLIO_STORE_NAMES.CATEGORIES)

            const request = objectStore.put(cleanCategory)

            request.onsuccess = () => {
                resolve()
            }

            request.onerror = (event) => {
                reject(event.target.error)
            }
        } else {
            try {
                // SQLite SDK 54 implementation
                const saveCategory = (category) => {
                    let subcategoriesIds = []
                    let productsIds = []

                    // Recorrer las subcategorías y guardarlas
                    if (category.categories && category.categories.length > 0) {
                        subcategoriesIds = category.categories.map((cat) => cat._id)
                        for (const subcategory of category.categories) {
                            saveCategory(subcategory)
                        }
                    }

                    // Obtener los IDs de los productos
                    if (category.products && category.products.length > 0) {
                        productsIds = category.products.map((prod) => prod._id)
                        for (const product of category.products) {
                            saveProduct(product)
                        }
                    }

                    // Insert category (remove duplicate brands)
                    const uniqueBrands = category.brands ? [...new Set(category.brands)] : []

                    const productsJson = JSON.stringify(productsIds)
                    console.log(`💾 [addCategory] "${category.name}" - products JSON length:`, productsJson.length, 'chars')

                    const statement = db.prepareSync(`INSERT OR REPLACE INTO ${PORTFOLIO_STORE_NAMES.CATEGORIES} (
                        _id, name, description_short, description_long, image, father_id,
                        categories, products, categoy_index, level, brands, index_category
                    ) VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?)`)

                    statement.executeSync([
                        String(category._id || ''),
                        String(category.name || ''),
                        String(category.description_short || ''),
                        String(category.description_long || ''),
                        String(category.image || ''),
                        String(category.father_id || ''),
                        JSON.stringify(subcategoriesIds),
                        productsJson,
                        category.index || 0,
                        category.level || 0,
                        JSON.stringify(uniqueBrands),
                        category.category_index || category.index_category || 0,
                    ])

                    statement.finalizeSync()
                }

                const saveProduct = (product) => {
                    const statement = db.prepareSync(`INSERT OR REPLACE INTO ${PORTFOLIO_STORE_NAMES.PRODUCTS} (
                        _id, name, description_short, description_long, brand, medias, ean, sku, download_links
                    ) VALUES (?, ?, ?, ?, ?, ?,?,?,?)`)

                    statement.executeSync([
                        String(product._id || ''),
                        String(product.name || ''),
                        String(product.description_short || ''),
                        String(product.description_long || ''),
                        String(product.brand_id || product.brand || ''),
                        String(JSON.stringify(product.medias) || ''),
                        String(product.ean || ''),
                        String(product.sku || ''),
                        String(JSON.stringify(product.download_links) || ''),
                    ])

                    statement.finalizeSync()
                }

                saveCategory(category)
                resolve()
            } catch (error) {
                console.error('Error in addCategory:', error)
                reject(error)
            }
        }
    })
}

export const getCategories = (db) => {
    return new Promise((resolve, reject) => {
        if (isWeb()) {
            const transaction = db.transaction([PORTFOLIO_STORE_NAMES.CATEGORIES], 'readonly')
            const objectStore = transaction.objectStore(PORTFOLIO_STORE_NAMES.CATEGORIES)
            const request = objectStore.getAll()

            request.onsuccess = (event) => {
                const categories = event.target.result
                // En web, las categorías ya vienen con la estructura correcta
                // Solo necesitamos asegurar que los arrays existen
                const normalizedCategories = categories.map((category) => ({
                    ...category,
                    categories: Array.isArray(category.categories) ? category.categories : [],
                    products: Array.isArray(category.products) ? category.products : [],
                    brands: Array.isArray(category.brands) ? category.brands : [],
                }))

                // Sort by index (categoy_index or index_category)
                const sortedCategories = normalizedCategories.sort((a, b) => {
                    const indexA = a.index_category ?? a.categoy_index ?? a.index ?? 0
                    const indexB = b.index_category ?? b.categoy_index ?? b.index ?? 0
                    return indexA - indexB
                })

                resolve(sortedCategories)
            }

            request.onerror = (event) => {
                reject(event.target.error)
            }
        } else {
            try {
                const statement = db.prepareSync(`SELECT * FROM ${PORTFOLIO_STORE_NAMES.CATEGORIES}`)
                // Use statement.getAllSync() which delegates to adapter.getAllSync and
                // avoids the intermediate executeSync result that may call native column APIs.
                let categories = []
                try {
                    categories = statement.getAllSync()
                } catch (innerErr) {
                    try {
                        const result = statement.executeSync()
                        categories = result?.getAllSync?.() || []
                    } catch (e) {
                        console.error('Nested error getting categories rows:', e)
                        categories = []
                    }
                }
                try {
                    statement.finalizeSync()
                } catch (e) {}

                // Parse JSON fields and normalize names used by UI
                const modifiedCategories = categories.map((category) => {
                    const parsedProducts = JSON.parse(category.products || '[]')

                    return {
                        ...category,
                        categories: JSON.parse(category.categories || '[]'),
                        products: parsedProducts,
                        brands: JSON.parse(category.brands || '[]'),
                    }
                })

                // Sort by index (categoy_index or index_category)
                const sortedCategories = modifiedCategories.sort((a, b) => {
                    const indexA = a.index_category ?? a.categoy_index ?? a.index ?? 0
                    const indexB = b.index_category ?? b.categoy_index ?? b.index ?? 0
                    return indexA - indexB
                })

                resolve(sortedCategories)
            } catch (error) {
                console.error('Error in getCategories:', error)
                reject(error)
            }
        }
    })
}

export const addBrand = (db, brand) => {
    return new Promise((resolve, reject) => {
        if (isWeb()) {
            const transaction = db.transaction([PORTFOLIO_STORE_NAMES.BRANDS], 'readwrite')
            const objectStore = transaction.objectStore(PORTFOLIO_STORE_NAMES.BRANDS)

            const request = objectStore.put(brand)

            request.onsuccess = () => {
                resolve()
            }

            request.onerror = (event) => {
                reject(event.target.error)
            }
        } else {
            try {
                const statement = db.prepareSync(`INSERT OR REPLACE INTO ${PORTFOLIO_STORE_NAMES.BRANDS} (
                    _id, status, name, index_brand
                ) VALUES (?, ?, ?, ?)`)

                statement.executeSync([String(brand._id || ''), brand.status || 1, String(brand.name || ''), brand.index_brand || 0])

                statement.finalizeSync()
                resolve()
            } catch (error) {
                console.error('Error in addBrand:', error)
                reject(error)
            }
        }
    })
}

export const getBrands = (db) => {
    return new Promise((resolve, reject) => {
        if (isWeb()) {
            const transaction = db.transaction([PORTFOLIO_STORE_NAMES.BRANDS], 'readonly')
            const objectStore = transaction.objectStore(PORTFOLIO_STORE_NAMES.BRANDS)
            const request = objectStore.getAll()

            request.onsuccess = (event) => {
                const brands = event.target.result
                // Sort by index_brand or index
                const sortedBrands = brands.sort((a, b) => {
                    const indexA = a.index_brand ?? a.index ?? 0
                    const indexB = b.index_brand ?? b.index ?? 0
                    return indexA - indexB
                })
                resolve(sortedBrands)
            }

            request.onerror = (event) => {
                reject(event.target.error)
            }
        } else {
            try {
                const statement = db.prepareSync(`SELECT * FROM ${PORTFOLIO_STORE_NAMES.BRANDS}`)
                // Prefer statement.getAllSync() from our adapter wrapper which directly
                // returns rows and avoids relying on the intermediate executeSync() result
                // that may call into native getColumnNamesSync and fail in some edge cases.
                let brands = []
                try {
                    brands = statement.getAllSync()
                } catch (innerErr) {
                    // Fallback: attempt executeSync().getAllSync() if available
                    try {
                        const result = statement.executeSync()
                        brands = result?.getAllSync?.() || []
                    } catch (e) {
                        console.error('Nested error getting brands rows:', e)
                        brands = []
                    }
                }
                try {
                    statement.finalizeSync()
                } catch (e) {}

                // Sort by index_brand or index
                const sortedBrands = brands.sort((a, b) => {
                    const indexA = a.index_brand ?? a.index ?? 0
                    const indexB = b.index_brand ?? b.index ?? 0
                    return indexA - indexB
                })

                resolve(sortedBrands)
            } catch (error) {
                console.error('Error in getBrands:', error)
                reject(error)
            }
        }
    })
}

// Placeholder functions for products functionality
export const addProduct = (db, product) => {
    return new Promise((resolve) => {
        console.log('⚠️  addProduct temporarily stubbed for SDK 54 migration')
        resolve()
    })
}

export const getProducts = (db) => {
    return new Promise((resolve, reject) => {
        if (isWeb()) {
            // En web no hay tabla separada de productos
            console.log('⚠️  getProducts not available in web (products are nested in categories)')
            resolve([])
        } else {
            try {
                const statement = db.prepareSync(`SELECT * FROM ${PORTFOLIO_STORE_NAMES.PRODUCTS}`)
                let products = []
                try {
                    products = statement.getAllSync()
                } catch (innerErr) {
                    try {
                        const result = statement.executeSync()
                        products = result?.getAllSync?.() || []
                    } catch (e) {
                        console.error('Nested error getting products rows:', e)
                        products = []
                    }
                }
                try {
                    statement.finalizeSync()
                } catch (e) {}

                // Sort by index_product or index
                const sortedProducts = products.sort((a, b) => {
                    const indexA = a.index_product ?? a.index ?? 0
                    const indexB = b.index_product ?? b.index ?? 0
                    return indexA - indexB
                })

                resolve(sortedProducts)
            } catch (error) {
                console.error('Error in getProducts:', error)
                reject(error)
            }
        }
    })
}

// Get products by their IDs (for native when categories store product IDs)
export const getProductsByIds = async (db, productIds) => {
    if (!productIds || productIds.length === 0) return []

    console.log('🔍 getProductsByIds called with IDs:', productIds)

    return new Promise((resolve, reject) => {
        if (isWeb()) {
            // En web los productos ya están en las categorías
            resolve([])
        } else {
            try {
                const allProducts = []

                // Fetch each product individually to avoid SQL parameter limits
                for (const productId of productIds) {
                    try {
                        const statement = db.prepareSync(`SELECT * FROM ${PORTFOLIO_STORE_NAMES.PRODUCTS} WHERE _id = ?`)

                        let products = []
                        try {
                            products = statement.getAllSync(productId)
                        } catch (innerErr) {
                            try {
                                const result = statement.executeSync(productId)
                                products = result?.getAllSync?.() || []
                            } catch (e) {
                                console.error(`Error getting product ${productId}:`, e)
                            }
                        }

                        if (products.length > 0) {
                            allProducts.push(products[0])
                        }

                        try {
                            statement.finalizeSync()
                        } catch (e) {}
                    } catch (err) {
                        console.error(`Error fetching product ${productId}:`, err)
                    }
                }

                console.log('🔍 getProductsByIds returned:', allProducts.length, 'products')
                if (allProducts.length > 0) {
                    console.log('🔍 First product:', allProducts[0]?.name)
                    console.log('🔍 Last product:', allProducts[allProducts.length - 1]?.name)
                }
                const parsedProducts = allProducts.map((p) => {
                    return { ...p, medias: JSON.parse(p.medias) || [], download_links: JSON.parse(p.download_links) || [] }
                })
                // Sort by index_product or index
                const sortedProducts = parsedProducts.sort((a, b) => {
                    const indexA = a.index_product ?? a.index ?? 0
                    const indexB = b.index_product ?? b.index ?? 0
                    return indexA - indexB
                })

                resolve(sortedProducts)
            } catch (error) {
                console.error('Error in getProductsByIds:', error)
                reject(error)
            }
        }
    })
}

// --- Compatibility helpers and aliases for existing UI imports ---
export const getProductsBycategoryId = async (db, categoryId) => {
    // In absence of products table relations, filter by stored products_ids from category
    const categories = await getCategories(db)
    const target = categories.find((c) => c._id === categoryId)
    if (!target) return []
    // No direct products getter implemented yet; return empty for now
    return []
}

export const getCategoryById = async (db, id) => {
    const categories = await getCategories(db)
    const item = categories.find((c) => c._id === id)
    if (!item) return null
    return { array_categories: [item] }
}

export const getCategoriesWithSubcategoriesAndProducts = async (db) => {
    const allCategories = await getCategories(db)

    // Helper: carga productos si vienen como IDs (solo en nativo)
    const loadProductsIfNeeded = async (products, categoryName = '') => {
        if (!Array.isArray(products) || products.length === 0) return []

        const firstProduct = products[0]
        const areProductIds = typeof firstProduct === 'string'

        if (areProductIds && !isWeb()) {
            try {
                const productObjects = await getProductsByIds(db, products)
                return productObjects
            } catch (error) {
                console.error(`❌ Error fetching products for category ${categoryName}:`, error)
                return []
            }
        } else {
            // Ya son objetos (web)
            return products
        }
    }

    // Helper recursivo para construir la jerarquía
    const buildCategoryTree = async (category, categoriesMap) => {
        const fullCategory = { ...category }

        // Cargar productos (si es necesario)
        fullCategory.products = await loadProductsIfNeeded(category.products || [], category.name)

        // Procesar subcategorías
        if (category.categories && Array.isArray(category.categories)) {
            const subcatPromises = category.categories.map(async (subcat) => {
                const subcatObj = typeof subcat === 'string' ? categoriesMap[subcat] : subcat
                return subcatObj ? await buildCategoryTree(subcatObj, categoriesMap) : null
            })
            fullCategory.categories = (await Promise.all(subcatPromises)).filter(Boolean)
        } else {
            fullCategory.categories = []
        }

        return fullCategory
    }

    // Mapa rápido de categorías por ID
    const categoriesMap = {}
    allCategories.forEach((cat) => {
        categoriesMap[cat._id] = cat
    })

    // Filtrar solo categorías raíz (nivel 0)
    const rootCategories = allCategories.filter((c) => c.level === 0)

    // Construir toda la jerarquía de categorías y subcategorías con productos
    const categoryTreePromises = rootCategories.map(async (cat, index) => {
        const built = await buildCategoryTree(cat, categoriesMap)
        return { ...built, index_category: index }
    })

    const categoryTree = await Promise.all(categoryTreePromises)

    return categoryTree
}

export const getCategoriesWithSubcategoriesAndProductsByBrandId = async (db, brandId) => {
    const allCategories = await getCategories(db)

    // console.log('🔍 Filtering by brandId:', brandId)
    // console.log('📦 Total categories loaded:', allCategories.length)

    // Helper function to check if a category or its descendants have products of the brand
    const categoryHasBrand = (category, categoriesMap) => {
        // Check if category has the brand in its brands array
        if (category.brands && Array.isArray(category.brands) && category.brands.includes(brandId)) {
            return true
        }

        // Check if any products belong to this brand
        if (category.products && Array.isArray(category.products)) {
            const hasMatchingProduct = category.products.some((product) => {
                // More flexible brand matching
                if (!product) return false
                const productBrand = product.brand || product.brand_id
                return productBrand === brandId
            })
            if (hasMatchingProduct) return true
        }

        // Check subcategories recursively
        if (category.categories && Array.isArray(category.categories)) {
            return category.categories.some((subcat) => {
                const subcatObj = typeof subcat === 'string' ? categoriesMap[subcat] : subcat
                return subcatObj && categoryHasBrand(subcatObj, categoriesMap)
            })
        }

        return false
    }

    // Helper to filter products by brand
    const filterProductsByBrand = async (products, categoryName = '') => {
        if (!Array.isArray(products) || products.length === 0) return []

        // Check if products are IDs (strings) or objects
        const firstProduct = products[0]
        const areProductIds = typeof firstProduct === 'string'

        console.log(`🔍 [${categoryName}] Products type:`, areProductIds ? 'IDs (Native)' : 'Objects (Web)')
        console.log(`🔍 [${categoryName}] Products count:`, products.length)

        if (areProductIds && !isWeb()) {
            // NATIVE: products are IDs, need to fetch from ecommerce_products table
            console.log(`🔍 [${categoryName}] Fetching ${products.length} products from ecommerce_products table`)
            console.log(`🔍 [${categoryName}] Product IDs to fetch:`, products)
            try {
                const productObjects = await getProductsByIds(db, products)
                console.log(`🔍 [${categoryName}] Fetched ${productObjects.length} product objects`)

                const filtered = productObjects.filter((product) => {
                    const productBrand = product.brand || product.brand_id
                    const matches = productBrand === brandId
                    console.log(`  ${matches ? '✅' : '❌'} [${categoryName}] Product: ${product.name}, brand: ${productBrand}`)
                    return matches
                })

                console.log(`🔍 [${categoryName}] Filtered products: ${filtered.length}/${productObjects.length}`)
                // Return IDs to maintain structure
                return filtered.map((p) => p._id)
            } catch (error) {
                console.error(`❌ Error fetching products for category ${categoryName}:`, error)
                return []
            }
        } else {
            // WEB: products are already objects
            const filtered = products.filter((product) => {
                if (!product) return false

                const productBrand = product.brand || product.brand_id
                const matches = productBrand === brandId

                if (!matches && products.length > 0) {
                    console.log(
                        `  ❌ Category "${categoryName}": Product "${product.name}" brand="${productBrand}" doesn't match brandId="${brandId}"`,
                    )
                } else if (matches) {
                    console.log(`  ✅ Category "${categoryName}": Product "${product.name}" matches!`)
                }

                return matches
            })

            return filtered
        }
    }

    // Helper to recursively filter categories and their children
    const filterCategory = async (category, categoriesMap) => {
        if (!categoryHasBrand(category, categoriesMap)) return null

        const filtered = { ...category }

        // Filter products (async now)
        const originalProductCount = (category.products || []).length
        filtered.products = await filterProductsByBrand(category.products || [], category.name)

        if (originalProductCount > 0) {
            console.log(`📂 Category "${category.name}": ${originalProductCount} products → ${filtered.products.length} after filter`)
        }

        // Filter and process subcategories (needs to handle async)
        if (category.categories && Array.isArray(category.categories)) {
            const subcatPromises = category.categories.map(async (subcat) => {
                const subcatObj = typeof subcat === 'string' ? categoriesMap[subcat] : subcat
                return subcatObj ? await filterCategory(subcatObj, categoriesMap) : null
            })
            filtered.categories = (await Promise.all(subcatPromises)).filter(Boolean)
        } else {
            filtered.categories = []
        }

        return filtered
    }

    // Create a map for quick lookups
    const categoriesMap = {}
    allCategories.forEach((cat) => {
        categoriesMap[cat._id] = cat
    })

    // Filter root categories (level 0)
    const rootCategories = allCategories.filter((c) => c.level === 0)

    console.log('🌳 Root categories:', rootCategories.length)

    // Filter each root category and its descendants (async now)
    const filteredCategoriesPromises = rootCategories.map((cat) => filterCategory(cat, categoriesMap))
    const filteredCategories = (await Promise.all(filteredCategoriesPromises)).filter(Boolean)

    console.log('✅ Filtered categories:', filteredCategories.length)

    return filteredCategories
}
